// src/pages/OnlineMenuManagement.tsx
import React, { useState, useEffect} from 'react'
import {
  getLocalSiteConfig,
} from '../../../../crud/management_api/local_site_configuration'
import MiniLoadingSpinner from '../../../../components/Spinners/MiniLoadingSpinner'
import SiteConfig from './components/SiteConfig/SiteConfig'
import './OnlineMenuManagement.scss'
import { Button } from 'react-bootstrap'
import { FaQrcode, FaCopy, FaMobileAlt } from 'react-icons/fa'
import { toast } from 'react-toastify'
// import { uuidv4 } from '../../../../utils/functions' // no longer needed
import { getLocalByLocalId } from '../../../../crud/management_api/locales'
import QRGenerator from './components/QRGenerator/QRGenerator'
import PreviewPhone from './components/Preview/PreviewPhone'
import DirectMenuSetting from './components/DirectMenuSetting'
import { isFeatureRolloutLocal, staffTipsSettingMessages } from '../../../../configuration/directMenuRollout'
import { isSuperAdmin } from '../../../../utils/functions'

const OnlineMenuManagement: React.FC = () => {
  const [config, setConfig] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showQR, setShowQR] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [isWide, setIsWide] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth >= 1000 : true)
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth <= 768 : false)
  const [isTablet, setIsTablet] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth > 768 && window.innerWidth < 1200 : false)
  const [reloadKey, setReloadKey] = useState<number>(Date.now())
  const [localSlug, setLocalSlug] = useState<string>('')
  const [siteUrl, setSiteUrl] = useState<string>('')
  const u = JSON.parse(localStorage.getItem('user') || '{}')
  const localId = u.local_id || 0
  // base url computed where needed


  // load site-config and local info on mount
  useEffect(() => {
    setLoading(true)
    const u = JSON.parse(localStorage.getItem('user') || '{}')
    const localId = u.local_id || 0

    Promise.all([
      getLocalSiteConfig(localId),
      getLocalByLocalId(String(localId))
    ])
      .then(([cfg, loc]: any[]) => {
        setConfig(cfg.data.object)
        const name: string = (loc.data?.local_name || 'local') as string
        const slug = String(name).replace(/\s+/g, '')
        setLocalSlug(slug)
        const origin = window.location.origin
        setSiteUrl(`${origin}/web/${slug}-${localId}`)
      })
      .catch(() => {
        toast.error('Error al cargar la configuración del sitio')
        setConfig(null)
      })
      .finally(() => {
        setLoading(false)
      })

  }, [])

  // track viewport width and handle responsive states
  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth
      setIsWide(width >= 1000)
      setIsMobile(width <= 768)
      setIsTablet(width > 768 && width < 1200)
      
      // Auto-hide preview on mobile and small tablets
      if (width <= 768 && showPreview) {
        setShowPreview(false)
      }
    }
    
    window.addEventListener('resize', onResize)
    onResize() // Call immediately to set initial state
    return () => window.removeEventListener('resize', onResize)
  }, [showPreview])

  if (!config || loading) return <MiniLoadingSpinner/>

  // Determine page layout class based on device type and preview state
  let pageClass = 'online-menu-management-page'
  if (showPreview) {
    if (isWide) {
      pageClass += ' has-preview-desktop'
    } else if (isTablet) {
      pageClass += ' has-preview-tablet'
    } else {
      pageClass += ' has-preview-mobile'
    }
  }

  return (
    <div className={pageClass}>
      <div className="online-menu-management-container">
      <h2>Configuración Mi punto digital</h2>

      <div className="info-container" style={{color:'var(--text-color)'}}>
        <div className="url-row">
          <a
            href={siteUrl}
            target='_blank'
            rel='noopener noreferrer'
            className="url-link"
            title={siteUrl}
          >
            {siteUrl}
          </a>
          <button
            className="copy-icon-btn"
            aria-label="Copiar enlace"
            onClick={async () => {
              try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  await navigator.clipboard.writeText(siteUrl)
                } else {
                  const el = document.createElement('textarea')
                  el.value = siteUrl
                  document.body.appendChild(el)
                  el.select()
                  document.execCommand('copy')
                  document.body.removeChild(el)
                }
                toast.success('Enlace copiado')
              } catch (err) {
                console.error('Error copiando enlace: ', err)
                toast.error('No se pudo copiar el enlace')
              }
            }}
          >
            <FaCopy />
          </button>
        </div>

        <div className='buttons-container'>
          <Button onClick={() => setShowQR(true)} variant="none" className="pill-button">
            <FaQrcode/> <span>Generar QR</span>
          </Button>
          <Button
            onClick={() => {
              if (isMobile) {
                // On mobile, navigate to the URL instead of showing preview
                window.open(siteUrl, '_blank')
              } else if (isTablet) {
                // On tablet, show modal-style preview
                setShowPreview(v => !v)
              } else {
                // On desktop, show side-by-side preview
                setShowPreview(v => !v)
              }
            }}
            variant="none"
            className="pill-button"
            title={
              isMobile 
                ? 'Abrir vista previa en nueva pestaña' 
                : isTablet 
                  ? 'Mostrar vista previa en modal'
                  : showPreview 
                    ? 'Ocultar vista previa lateral' 
                    : 'Mostrar vista previa lateral'
            }
          >
            <FaMobileAlt/> 
            <span>
              {isMobile 
                ? 'Pre-visualización' 
                : isTablet 
                  ? (showPreview ? 'Ocultar vista previa' : 'Vista previa')
                  : (showPreview ? 'Ocultar vista previa' : 'Vista previa lateral')
              }
            </span>
          </Button>
        </div>
      </div>

      {/* 1) SiteConfig always */}
      {isFeatureRolloutLocal(localId) && isSuperAdmin() && (
        <>
          <DirectMenuSetting key={`menu-${localId}`} localId={localId} onSaved={() => setReloadKey(Date.now())} />
          <DirectMenuSetting key={`tips-${localId}`} localId={localId} feature="staff_tips"
            copy={staffTipsSettingMessages} onSaved={() => setReloadKey(Date.now())} />
        </>
      )}
      <SiteConfig
        config={config}
        setConfig={setConfig}
        onConfigChanged={() => setReloadKey(Date.now())}
      />
      <QRGenerator
        url={siteUrl}
        showModal={showQR}
        onClose={() => setShowQR(false)}
      />

      </div>
      {/* 2) Phone preview - adaptive rendering based on device type */}
      {!isMobile && (
        <PreviewPhone
          localId={localId}
          localSlug={localSlug}
          visible={showPreview}
          reloadKey={reloadKey}
          onClose={() => setShowPreview(false)}
          deviceType={isTablet ? 'tablet' : 'desktop'}
          isModal={isTablet}
        />
      )}
    </div>
  )
}

export default OnlineMenuManagement
